import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Every backend error envelope in this app is bilingual (`message` +
 * `message_ar` — see CLAUDE.md). Picks whichever matches the active language,
 * falling back to the other one if that field is missing, and finally to a
 * caller-supplied default so a response with neither never renders as blank.
 *
 * Centralised so useEmailVerification's `messageFor` and the two register
 * error handlers (AuthPage, SupplierRegister) share one fallback order instead
 * of three slightly different hand-rolled ternaries drifting apart over time.
 */
export function localizedApiMessage(
  payload: { message?: string; message_ar?: string } | null | undefined,
  isArabic: boolean,
  fallback: string
): string {
  const localized = isArabic
    ? payload?.message_ar || payload?.message
    : payload?.message || payload?.message_ar
  return localized || fallback
}

export function formatDate(dateString: string, locale: string = 'en'): string {
  const date = new Date(dateString)
  
  if (locale === 'ar') {
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

export function formatRelativeTime(dateString: string, locale: string = 'en'): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffInMs = now.getTime() - date.getTime()
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))
  
  if (diffInDays === 0) {
    return locale === 'ar' ? 'اليوم' : 'Today'
  } else if (diffInDays === 1) {
    return locale === 'ar' ? 'أمس' : 'Yesterday'
  } else if (diffInDays < 7) {
    return locale === 'ar' ? `منذ ${diffInDays} أيام` : `${diffInDays} days ago`
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7)
    return locale === 'ar' ? `منذ ${weeks} أسبوع` : `${weeks} week${weeks > 1 ? 's' : ''} ago`
  } else {
    return formatDate(dateString, locale)
  }
}
